import { useState, useMemo } from 'react';
import {
  Title,
  Text,
  Stack,
  Paper,
  Table,
  Button,
  Group,
  ActionIcon,
  Modal,
  TextInput,
  Textarea,
  Select,
  Badge,
  Skeleton,
  Alert,
  Card,
  SimpleGrid,
  Switch,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconUsers,
  IconBriefcase,
  IconSchool,
  IconUserQuestion,
  IconSearch,
  IconAlertCircle,
} from '@tabler/icons-react';

import {
  useBorrowers,
  useBorrowerTypes,
  useCreateBorrower,
  useUpdateBorrower,
  useDeleteBorrower,
} from '../hooks/useLoans';
import { Borrower } from '../types';

const TYPE_ICONS: Record<string, typeof IconUsers> = {
  employee: IconBriefcase,
  student: IconSchool,
  external: IconUserQuestion,
};

const TYPE_COLORS: Record<string, string> = {
  employee: 'blue',
  student: 'green',
  external: 'orange',
};

interface BorrowerFormData {
  borrowerType: string;
  identificationNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  department: string;
  notes: string;
}

const emptyFormData: BorrowerFormData = {
  borrowerType: 'employee',
  identificationNumber: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  department: '',
  notes: '',
};

export function BorrowersPage() {
  // Filters
  const [searchFilter, setSearchFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modal state
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [editingBorrower, setEditingBorrower] = useState<Borrower | null>(null);
  const [deletingBorrower, setDeletingBorrower] = useState<Borrower | null>(null);

  // Form data
  const [formData, setFormData] = useState<BorrowerFormData>(emptyFormData);

  // Queries
  const filters = useMemo(() => {
    const f: { borrowerType?: string; search?: string; isActive?: boolean } = {};
    if (typeFilter) f.borrowerType = typeFilter;
    if (searchFilter) f.search = searchFilter;
    if (!showInactive) f.isActive = true;
    return f;
  }, [typeFilter, searchFilter, showInactive]);

  const { data: borrowers, isLoading, error } = useBorrowers(filters);
  const { data: borrowerTypes } = useBorrowerTypes();

  // Mutations
  const createMutation = useCreateBorrower();
  const updateMutation = useUpdateBorrower();
  const deleteMutation = useDeleteBorrower();

  // Handlers
  const handleOpenCreate = () => {
    setEditingBorrower(null);
    setFormData(emptyFormData);
    openCreate();
  };

  const handleOpenEdit = (borrower: Borrower) => {
    setEditingBorrower(borrower);
    setFormData({
      borrowerType: borrower.borrowerType,
      identificationNumber: borrower.identificationNumber,
      firstName: borrower.firstName,
      lastName: borrower.lastName,
      email: borrower.email || '',
      phone: borrower.phone || '',
      department: borrower.department || '',
      notes: borrower.notes || '',
    });
    openCreate();
  };

  const handleOpenDelete = (borrower: Borrower) => {
    setDeletingBorrower(borrower);
    openDelete();
  };

  const handleSubmit = async () => {
    try {
      if (editingBorrower) {
        await updateMutation.mutateAsync({
          id: editingBorrower.id,
          data: {
            borrowerType: formData.borrowerType,
            identificationNumber: formData.identificationNumber,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email || null,
            phone: formData.phone || null,
            department: formData.department || null,
            notes: formData.notes || null,
          },
        });
        notifications.show({
          title: 'Uspeh',
          message: 'Izposojevalec posodobljen',
          color: 'green',
        });
      } else {
        await createMutation.mutateAsync({
          borrowerType: formData.borrowerType,
          identificationNumber: formData.identificationNumber,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          department: formData.department || undefined,
          notes: formData.notes || undefined,
        });
        notifications.show({
          title: 'Uspeh',
          message: 'Izposojevalec dodan',
          color: 'green',
        });
      }
      closeCreate();
      setFormData(emptyFormData);
    } catch (error) {
      notifications.show({
        title: 'Napaka',
        message: error instanceof Error ? error.message : 'Napaka pri shranjevanju',
        color: 'red',
      });
    }
  };

  const handleDelete = async () => {
    if (!deletingBorrower) return;
    try {
      const result = await deleteMutation.mutateAsync(deletingBorrower.id);
      notifications.show({
        title: 'Uspeh',
        message: result.deactivated ? 'Izposojevalec deaktiviran (ima zgodovino izposoj)' : 'Izposojevalec izbrisan',
        color: 'green',
      });
      closeDelete();
      setDeletingBorrower(null);
    } catch (error) {
      notifications.show({
        title: 'Napaka',
        message: error instanceof Error ? error.message : 'Napaka pri brisanju',
        color: 'red',
      });
    }
  };

  // Stats
  const stats = useMemo(() => {
    if (!borrowers) return { total: 0, byType: {} as Record<string, number> };
    const byType: Record<string, number> = {};
    borrowers.forEach((b) => {
      byType[b.borrowerType] = (byType[b.borrowerType] || 0) + 1;
    });
    return { total: borrowers.length, byType };
  }, [borrowers]);

  const typeOptions = useMemo(() => {
    return borrowerTypes?.map((t) => ({ value: t.value, label: t.label })) || [];
  }, [borrowerTypes]);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Title order={2}>Izposojevalci</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={handleOpenCreate}>
          Dodaj izposojevalca
        </Button>
      </Group>

      {/* Stats Cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Skupaj
              </Text>
              <Text size="xl" fw={700}>
                {stats.total}
              </Text>
            </div>
            <IconUsers size={32} color="var(--mantine-color-gray-5)" />
          </Group>
        </Card>
        {['employee', 'student', 'external'].map((type) => {
          const Icon = TYPE_ICONS[type] || IconUsers;
          const label = borrowerTypes?.find((t) => t.value === type)?.label || type;
          return (
            <Card withBorder key={type}>
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    {label}
                  </Text>
                  <Text size="xl" fw={700}>
                    {stats.byType[type] || 0}
                  </Text>
                </div>
                <Icon size={32} color={`var(--mantine-color-${TYPE_COLORS[type]}-5)`} />
              </Group>
            </Card>
          );
        })}
      </SimpleGrid>

      {/* Filters */}
      <Paper withBorder p="md">
        <Group>
          <TextInput
            placeholder="Iskanje..."
            leftSection={<IconSearch size={16} />}
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            style={{ flex: 1, maxWidth: 300 }}
          />
          <Select
            placeholder="Vsi tipi"
            clearable
            value={typeFilter}
            onChange={(v) => setTypeFilter(v || '')}
            data={typeOptions}
            style={{ width: 180 }}
          />
          <Switch
            label="Prikazi neaktivne"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.currentTarget.checked)}
          />
        </Group>
      </Paper>

      {/* Table */}
      <Paper withBorder>
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} m="md">
            Napaka pri nalaganju: {error instanceof Error ? error.message : 'Neznana napaka'}
          </Alert>
        )}

        {isLoading ? (
          <Stack p="md">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} height={40} />
            ))}
          </Stack>
        ) : borrowers && borrowers.length > 0 ? (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tip</Table.Th>
                <Table.Th>ID številka</Table.Th>
                <Table.Th>Ime in priimek</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Oddelek</Table.Th>
                <Table.Th>Aktivne izposoje</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th style={{ width: 100 }}>Akcije</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {borrowers.map((borrower) => {
                const Icon = TYPE_ICONS[borrower.borrowerType] || IconUsers;
                return (
                  <Table.Tr key={borrower.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <Icon size={16} />
                        <Badge color={TYPE_COLORS[borrower.borrowerType]} variant="light" size="sm">
                          {borrower.borrowerTypeLabel}
                        </Badge>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" size="sm">
                        {borrower.identificationNumber}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={500}>{borrower.fullName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {borrower.email || '-'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{borrower.department || '-'}</Text>
                    </Table.Td>
                    <Table.Td>
                      {borrower.activeLoansCount > 0 ? (
                        <Badge color="orange">{borrower.activeLoansCount}</Badge>
                      ) : (
                        <Text size="sm" c="dimmed">
                          0
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge color={borrower.isActive ? 'green' : 'gray'}>
                        {borrower.isActive ? 'Aktiven' : 'Neaktiven'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label="Uredi">
                          <ActionIcon variant="subtle" onClick={() => handleOpenEdit(borrower)}>
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Izbriši">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => handleOpenDelete(borrower)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        ) : (
          <Stack align="center" p="xl">
            <IconUsers size={48} color="var(--mantine-color-gray-5)" />
            <Text c="dimmed">Ni izposojevalcev</Text>
            <Button onClick={handleOpenCreate}>Dodaj prvega izposojevalca</Button>
          </Stack>
        )}
      </Paper>

      {/* Create/Edit Modal */}
      <Modal
        opened={createOpened}
        onClose={closeCreate}
        title={editingBorrower ? 'Uredi izposojevalca' : 'Nov izposojevalec'}
        size="lg"
      >
        <Stack>
          <Group grow>
            <Select
              label="Tip"
              required
              value={formData.borrowerType}
              onChange={(v) => setFormData({ ...formData, borrowerType: v || 'employee' })}
              data={typeOptions}
            />
            <TextInput
              label="ID številka"
              required
              placeholder="Zaposleni ID ali vpisna številka"
              value={formData.identificationNumber}
              onChange={(e) => setFormData({ ...formData, identificationNumber: e.target.value })}
            />
          </Group>

          <Group grow>
            <TextInput
              label="Ime"
              required
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            />
            <TextInput
              label="Priimek"
              required
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            />
          </Group>

          <Group grow>
            <TextInput
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <TextInput
              label="Telefon"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </Group>

          <TextInput
            label="Oddelek / Razred"
            value={formData.department}
            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
          />

          <Textarea
            label="Opombe"
            minRows={2}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closeCreate}>
              Prekliči
            </Button>
            <Button
              onClick={handleSubmit}
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editingBorrower ? 'Shrani' : 'Dodaj'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Modal */}
      <Modal
        opened={deleteOpened}
        onClose={closeDelete}
        title="Izbriši izposojevalca"
        size="sm"
      >
        <Stack>
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {deletingBorrower?.activeLoansCount && deletingBorrower.activeLoansCount > 0 ? (
              <>
                Ta izposojevalec ima <strong>{deletingBorrower.activeLoansCount} aktivnih izposoj</strong>.
                Najprej vrnite vso opremo.
              </>
            ) : (
              <>
                Ali ste prepričani, da želite izbrisati izposojevalca{' '}
                <strong>{deletingBorrower?.fullName}</strong>?
              </>
            )}
          </Alert>

          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDelete}>
              Prekliči
            </Button>
            <Button
              color="red"
              onClick={handleDelete}
              loading={deleteMutation.isPending}
              disabled={
                deletingBorrower?.activeLoansCount !== undefined &&
                deletingBorrower.activeLoansCount > 0
              }
            >
              Izbriši
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
